import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ArrowRight, Key } from 'lucide-react';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Watermark from '../components/Watermark';

const OTPVerification = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '']);
  const [apiKey, setApiKey] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [step, setStep] = useState('otp'); // 'otp' or 'apikey'

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleOtpChange = (index, value) => {
    // SMS auto-fill: distribute pasted digits across boxes
    if (value.length > 1 && /^[0-9]+$/.test(value)) {
      const digits = value.slice(0, 4).split('');
      const newOtp = ['', '', '', ''];
      digits.forEach((d, i) => { newOtp[i] = d; });
      setOtp(newOtp);
      const lastIdx = Math.min(digits.length, 4) - 1;
      if (lastIdx >= 0 && lastIdx < 3) document.getElementById(`otp-${lastIdx + 1}`)?.focus();
      else document.getElementById(`otp-3`)?.blur();
      return;
    }
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 3) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleResend = () => {
    if (canResend) {
      setTimer(30);
      setCanResend(false);
      toast.success('OTP resent successfully!');
    }
  };

  const handleOtpContinue = async () => {
    const otpValue = otp.join('');
    if (otpValue.length === 4) {
      // For demo, accept any 4-digit OTP
      localStorage.setItem('user_authenticated', 'true');
      toast.success('✅ OTP verified!');
      
      // Skip API key step - proceed directly to story generation
      const birthData = JSON.parse(localStorage.getItem('birthData') || '{}');
      const birthCoords = JSON.parse(localStorage.getItem('birthCoordinates') || '{}');
      
      const storyData = {
        name: localStorage.getItem('userName') || 'User',
        dateOfBirth: birthData.date,
        timeOfBirth: birthData.time,
        placeOfBirth: birthCoords.place_name || localStorage.getItem('placeOfBirth') || 'Unknown',
        latitude: birthCoords.latitude || 28.6139,
        longitude: birthCoords.longitude || 77.2090
      };
      
      try {
        toast.info('🔮 Generating your past story...');
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
        // Don't send API key - backend will use Universal Key automatically
        const response = await fetch(`${backendUrl}/api/story/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(storyData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to start story generation');
        }
        
        const data = await response.json();
        localStorage.setItem('prediction_id', data.prediction_id);
        
        toast.success('✨ Story generation started!');
        navigate('/lets-explore');
        
      } catch (err) {
        console.error('Story start error:', err);
        toast.error('Failed to start story: ' + err.message);
      }
    } else {
      toast.error('Please enter complete OTP');
    }
  };

  const handleApiKeyContinue = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter your OpenAI API key');
      return;
    }
    
    if (!apiKey.startsWith('sk-proj-') && !apiKey.startsWith('sk-')) {
      toast.error('Invalid OpenAI API key format');
      return;
    }
    
    // Save API key
    localStorage.setItem('openai_api_key', apiKey.trim());
    
    // Prepare birth data for story generation
    const birthData = JSON.parse(localStorage.getItem('birthData') || '{}');
    const birthCoords = JSON.parse(localStorage.getItem('birthCoordinates') || '{}');
    
    const storyData = {
      name: localStorage.getItem('userName') || 'User',
      dateOfBirth: birthData.date,
      timeOfBirth: birthData.time,
      placeOfBirth: birthCoords.place_name || localStorage.getItem('placeOfBirth') || 'Unknown',
      latitude: birthCoords.latitude || 28.6139,
      longitude: birthCoords.longitude || 77.2090
    };
    
    try {
      toast.info('🔮 Generating your past story...');
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${backendUrl}/api/story/start?openai_api_key=${encodeURIComponent(apiKey.trim())}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(storyData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to start story generation');
      }
      
      const data = await response.json();
      localStorage.setItem('prediction_id', data.prediction_id);
      
      toast.success('✨ Story generation started!');
      navigate('/lets-explore');
      
    } catch (err) {
      console.error('Story start error:', err);
      toast.error('Failed to start story: ' + err.message);
    }
  };

  if (step === 'apikey') {
    return (
      <CosmicBackground showAstroChart={false}>
        <Watermark position="bottom-right" opacity={0.3} />
        
        <div className="min-h-screen relative" data-testid="api-key-screen">
          {/* Back button */}
          <button 
            onClick={() => setStep('otp')} 
            className="absolute top-14 left-4 text-white z-20"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Content */}
          <div className="flex flex-col h-screen pt-24 px-6">
            {/* Title */}
            <h1 
              className="text-white text-3xl font-serif text-center mb-3"
              style={{letterSpacing: '0.1em'}}
            >
              OPENAI API KEY
            </h1>

            {/* Subtitle */}
            <p className="text-gray-400 text-center text-sm mb-2 px-4">
              Enter your OpenAI API key to generate your personalized story
            </p>
            <p className="text-[#D4AF37] text-center text-xs mb-8">
              Get your key from platform.openai.com
            </p>

            {/* API Key Input */}
            <div className="mb-6">
              <label className="text-gray-300 text-sm mb-2 block">OpenAI API Key</label>
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-proj-..."
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                  autoFocus
                />
              </div>
            </div>
            
            {/* Info Box */}
            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <p className="text-blue-300 text-xs leading-relaxed">
                💡 Your API key is stored locally and used only to generate your astrological story. We never store it on our servers.
              </p>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Continue button */}
            <button
              onClick={handleApiKeyContinue}
              disabled={!apiKey.trim()}
              className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-[#0a1628] rounded-full font-semibold text-lg flex items-center justify-center gap-2 mb-8 disabled:opacity-50 hover:scale-105 transition-transform shadow-lg"
            >
              Start Story Generation
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </CosmicBackground>
    );
  }

  return (
    <CosmicBackground showAstroChart={false}>
      <Watermark position="bottom-right" opacity={0.3} />
      
      <div className="min-h-screen relative" data-testid="otp-screen">
        {/* Back button */}
        <button 
          onClick={() => navigate(-1)} 
          className="absolute top-14 left-4 text-white z-20"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="flex flex-col h-screen pt-24 px-6">
          {/* Title */}
          <h1 
            className="text-white text-3xl font-serif text-center mb-3"
            style={{letterSpacing: '0.1em'}}
          >
            ENTER CODE
          </h1>

          {/* Subtitle */}
          <p className="text-gray-400 text-center text-sm mb-2">
            We sent a verification code to your phone number
          </p>
          <p className="text-[#D4AF37] text-center text-sm mb-12">
            +91 123-343-4323
          </p>

          {/* OTP Input */}
          <div className="flex gap-4 justify-center mb-8">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                name={index === 0 ? 'one-time-code' : `otp-${index}`}
                pattern="[0-9]*"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-16 h-16 text-center text-2xl font-bold bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                maxLength={index === 0 ? 4 : 1}
              />
            ))}
          </div>

          {/* Timer / Resend */}
          <div className="text-center mb-12">
            {!canResend ? (
              <p className="text-gray-400 text-sm">
                Resend code in <span className="text-[#D4AF37] font-semibold">{timer}s</span>
              </p>
            ) : (
              <button
                onClick={handleResend}
                className="text-[#D4AF37] text-sm font-semibold hover:underline"
              >
                Resend Code
              </button>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Continue button */}
          <button
            onClick={handleOtpContinue}
            disabled={otp.join('').length !== 4}
            className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-[#0a1628] rounded-full font-semibold text-lg flex items-center justify-center gap-2 mb-8 disabled:opacity-50 hover:scale-105 transition-transform shadow-lg"
          >
            Verify & Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Bottom indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-white/30 rounded-full" />
      </div>
    </CosmicBackground>
  );
};

export default OTPVerification;
